import { useCallback } from "react";

import { useAssetUploader } from "@skilitics/assets";
import type { SlotId } from "@skilitics/nautilus-core-designer-data";

import { augmentMediaNodeConfig, mapContentTypeToMediaType, mapMediaTypeToNodeTypeId } from "../media/index.ts";
import { useCreateNode, useMutateNodeDesigns } from "../state/index.ts";
import type { MediaConfig } from "./types.ts";
import { useSlotDesigner } from "./useSlotDesigner.ts";

// note: async signatures to allow for more complex tidy ups
export const useAssetSlotDesigner = (slotId: SlotId) => {
  const { add: addNode, update: updateNode } = useMutateNodeDesigns();
  const createNode = useCreateNode();
  const { resident, removeSlotResident } = useSlotDesigner(slotId);
  const uploader = useAssetUploader();

  const add = useCallback(
    async (file: File) => {
      const { assetId } = await uploader.receiveFile(file);

      const mediaType = mapContentTypeToMediaType(file.type);
      const nodeTypeId = mapMediaTypeToNodeTypeId(mediaType);

      if (resident) {
        if (resident.typeId === nodeTypeId) {
          const mediaNode = { ...resident, config: { ...resident.config, assetId, name: file.name } };
          const { config } = await augmentMediaNodeConfig(file, mediaNode);
          updateNode(resident.id, config);
          return;
        }

        removeSlotResident();
      }

      const mediaNode = createNode(nodeTypeId, { assetId, name: file.name });
      addNode(await augmentMediaNodeConfig(file, mediaNode), slotId);
    },
    [addNode, createNode, removeSlotResident, resident, slotId, updateNode, uploader],
  );

  const remove = useCallback(() => {
    removeSlotResident();
    return Promise.resolve();
  }, [removeSlotResident]);

  const update = useCallback(
    (updated: MediaConfig) => {
      if (!resident) return;
      updateNode(resident.id, { ...resident.config, ...updated });
    },
    [resident, updateNode],
  );

  return {
    add,
    remove,
    update,
    cancelAdd: uploader.cancelUpload,
  };
};
